import React, { useEffect, useState } from 'react'
import { useAppSelector } from '../../store/hooks'
import useCsrfToken from '../../hooks/useCsrfToken'

interface AdminUser {
  id: number
  email: string
  admin: boolean
  created_at: string
}

const AdminUsers: React.FC = () => {
  const currentUser = useAppSelector((s) => s.auth.user)
  const csrfToken = useCsrfToken()

  const [users, setUsers] = useState<AdminUser[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [newEmail, setNewEmail] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [createError, setCreateError] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)

  const loadUsers = () => {
    fetch('/api/admin/users', { headers: { Accept: 'application/json' } })
      .then((r) => r.json())
      .then((data) => { setUsers(data); setLoading(false) })
      .catch(() => { setError('Failed to load users'); setLoading(false) })
  }

  useEffect(() => { loadUsers() }, [])

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    setCreating(true)
    setCreateError(null)
    const res = await fetch('/api/admin/users', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-CSRF-Token': csrfToken,
      },
      body: JSON.stringify({ email: newEmail, password: newPassword }),
    })
    const data = await res.json()
    setCreating(false)
    if (res.ok) {
      setUsers((prev) => [...prev, data].sort((a, b) => a.email.localeCompare(b.email)))
      setNewEmail('')
      setNewPassword('')
    } else {
      setCreateError(data.errors?.join(', ') || data.error || 'Failed to create user')
    }
  }

  const handleDelete = async (user: AdminUser) => {
    if (!confirm(`Delete ${user.email}? This cannot be undone.`)) return
    const res = await fetch(`/api/admin/users/${user.id}`, {
      method: 'DELETE',
      headers: { Accept: 'application/json', 'X-CSRF-Token': csrfToken },
    })
    if (res.ok) {
      setUsers((prev) => prev.filter((u) => u.id !== user.id))
    }
  }

  return (
    <div className="max-w-2xl mx-auto space-y-8">
      <div className="bg-navy-900 border border-army-800 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-army-800">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">All accounts</p>
        </div>
        {loading ? (
          <p className="px-4 py-6 text-gray-500 text-sm">Loading…</p>
        ) : error ? (
          <p className="px-4 py-6 text-rose-400 text-sm">{error}</p>
        ) : (
          <ul className="divide-y divide-army-800">
            {users.map((user) => (
              <li key={user.id} className="flex items-center justify-between px-4 py-3">
                <div className="flex items-center gap-3 min-w-0">
                  <span className="text-sm text-stone-200 truncate">{user.email}</span>
                  {user.admin && (
                    <span className="text-xs px-1.5 py-0.5 rounded bg-army-800 text-neon font-medium shrink-0">
                      admin
                    </span>
                  )}
                </div>
                <button
                  onClick={() => handleDelete(user)}
                  disabled={user.id === currentUser?.id}
                  className="text-xs text-gray-600 hover:text-rose-400 transition-colors disabled:opacity-30 disabled:cursor-not-allowed shrink-0 ml-4"
                >
                  Delete
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="bg-navy-900 border border-army-800 rounded-lg p-4">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4">Add user</p>
        <form onSubmit={handleCreate} className="space-y-3">
          <input
            type="email"
            required
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            placeholder="Email address"
            className="w-full px-3 py-2 bg-army-950 border border-army-800 rounded-lg text-white placeholder-gray-600 focus:outline-none focus:border-neon transition-colors text-sm"
          />
          <input
            type="password"
            required
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder="Temporary password"
            className="w-full px-3 py-2 bg-army-950 border border-army-800 rounded-lg text-white placeholder-gray-600 focus:outline-none focus:border-neon transition-colors text-sm"
          />
          {createError && <p className="text-sm text-rose-400">{createError}</p>}
          <button
            type="submit"
            disabled={creating}
            className="px-4 py-2 bg-neon text-army-950 font-semibold rounded-lg text-sm hover:opacity-90 transition-opacity disabled:opacity-50"
          >
            {creating ? 'Creating…' : 'Create account'}
          </button>
        </form>
      </div>
    </div>
  )
}

export default AdminUsers
